/**
 * Helper functions for Payroll Reports
 */

/**
 * Get initial state from storage (session or local)
 */
const STATE_VERSION = '1.1'; // Increment this to force clear old corrupted data

export const getInitialState = () => {
  try {
    const sessionState = sessionStorage.getItem('payrollReportsState');
    const localState = localStorage.getItem('payrollReportsState');
    const savedState = sessionState || localState;
    
    if (savedState && savedState !== 'null' && savedState !== '{}') {
      const parsedState = JSON.parse(savedState);
      
      // Check version - clear old data if version doesn't match
      if (parsedState.version !== STATE_VERSION) {
        console.log('Clearing old payroll state due to version mismatch');
        sessionStorage.removeItem('payrollReportsState');
        localStorage.removeItem('payrollReportsState');
        return null;
      }
      
      // Check if the saved state is recent (within last 10 minutes)
      const isRecentSave = parsedState.timestamp && (Date.now() - parsedState.timestamp) < 10 * 60 * 1000;
      
      if (isRecentSave) {
        // Sanitize payrollData to remove null/undefined entries from customEntries arrays
        if (parsedState.payrollData && Array.isArray(parsedState.payrollData)) {
          parsedState.payrollData = parsedState.payrollData.map(job => {
            if (job.breakdownDetails && job.breakdownDetails.customEntries) {
              job.breakdownDetails.customEntries = job.breakdownDetails.customEntries.filter(entry => entry != null);
            }
            return job;
          });
        }
        return parsedState;
      }
    }
  } catch (error) {
    console.error("Error parsing initial state from storage:", error);
    // Clear corrupted data
    sessionStorage.removeItem('payrollReportsState');
    localStorage.removeItem('payrollReportsState');
  }
  return null;
};

/**
 * Get minimum end date based on start date
 */
export const getMinEndDate = (startDate) => {
  if (!startDate) return '';
  return startDate;
};

/**
 * Convert payroll spans to rows compatible with payrollData structure
 */
export const spansToPayrollRows = (spans = []) =>
  spans.map((s) => {
    const startHM = (s.start_iso || "").slice(11, 16);
    const endHM = (s.end_iso || "").slice(11, 16);

    return {
      id: s.job_order_id || s.id || "SPAN",
      job_date: s.payroll_date,
      start_time: startHM,
      end_time: endHM,
      client: s.client_name || "",
      job_types: Array.isArray(s.raw?.job_types) ? s.raw.job_types : [],
      workHours: typeof s.hours === "number" ? +s.hours : (s.minutes || 0) / 60,
      regularHours: typeof s.hours === "number" ? +s.hours : (s.minutes || 0) / 60,
      form_type: s.form_type || s.raw?.form_type || null,
    };
  });

/**
 * Generate all dates in a range
 */
export const getAllDatesInRange = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  const end = new Date(endDate);
  
  while (currentDate <= end) {
    dates.push(new Date(currentDate).toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

/**
 * Format hourly rate with commas for display
 */
export const formatHourlyRateForDisplay = (value) => {
  if (!value) return '';
  
  const numericValue = value.toString().replace(/,/g, '');
  if (isNaN(numericValue)) return value;
  
  const parts = numericValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return parts.join('.');
};

/**
 * Format amount for display with commas
 */
export const formatAmountForDisplay = (value) => {
  if (!value) return '';
  
  const numericValue = value.toString().replace(/,/g, '');
  if (isNaN(numericValue)) return value;
  
  const parts = numericValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return parts.join('.');
};

/**
 * Get display value for input field based on focus state
 */
export const getDisplayValue = (focusedField, fieldName, value) => {
  if (focusedField === fieldName) {
    return value;
  }
  return formatAmountForDisplay(value);
};

/**
 * Map form_type to URL segment
 */
export const getFormTypeSegment = (formType) => {
  const formTypeToSegment = {
    makati_gpc_form:       "makati-gpc-form",
    qc_form:               "qc-form", 
    termite_control_form:  "termite-control-form",
    transmittal_form:      "transmittal-form",
  };
  
  return formTypeToSegment[formType] || "job-order";
};

/**
 * Get formatted date and time string
 */
export const getFormattedDateTime = () => {
  const now = new Date();
  return now.toLocaleString('en-US', { 
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get default date range (first day of current month to last day)
 */
export const getDefaultDateRange = () => {
  const now = new Date();
  const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  
  // Start date: first day of current month
  const startDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    1,
    0,
    0,
    0,
    0
  );
  startDate.setMinutes(startDate.getMinutes() + philippineTimezoneOffset);
  
  // End date: last day of current month
  const endDate = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    0,
    0,
    0,
    0
  );
  endDate.setMinutes(endDate.getMinutes() + philippineTimezoneOffset);
  
  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0]
  };
};

/**
 * Save state to storage
 */
export const saveStateToStorage = (state) => {
  const stateToSave = {
    ...state,
    timestamp: Date.now()
  };
  
  console.log("Saving state to storage:", stateToSave);
  
  sessionStorage.setItem('payrollReportsState', JSON.stringify(stateToSave));
  localStorage.setItem('payrollReportsState', JSON.stringify(stateToSave));
};

/**
 * Clear state from storage
 */
export const clearStateFromStorage = () => {
  sessionStorage.removeItem('payrollReportsState');
  localStorage.removeItem('payrollReportsState');
};
